import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Card } from 'antd';
import { useLocation } from 'react-router-dom';

interface LocationResponse {
  country: string;
}

const US_COUNTRY_CODE = 'US';

const AUTH_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/create-password',
  '/verify-email',
];

function isLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  try {
    const key = '__zoomlogi_cookie_banner_storage_test__';
    window.localStorage.setItem(key, key);
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function hasAcceptedCookies(): boolean {
  return isLocalStorageAvailable()
    ? window.localStorage.getItem('cookiesAccepted') === 'true'
    : true;
}

function setCookiesAccepted(): void {
  if (isLocalStorageAvailable()) {
    window.localStorage.setItem('cookiesAccepted', 'true');
  }
}

/**
 * Cookie consent banner for non-US users
 *
 * Displays a cookie consent banner for users outside the United States
 * and stores their consent in localStorage
 */
const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const location = useLocation();
  const hasCheckedLocationRef = useRef<boolean>(false);

  const isAuthRoute = AUTH_PATH_PREFIXES.some((path) =>
    location.pathname.startsWith(path)
  );

  useEffect(() => {
    let isMounted = true;

    const checkUserLocation = async (): Promise<void> => {
      if (isAuthRoute || hasAcceptedCookies()) {
        setShowBanner(false);
        return;
      }

      // Only need to resolve the visitor's country once per session —
      // isAuthRoute can toggle multiple times across login/logout without
      // re-triggering the geolocation lookup. Only latch this on success:
      // a transient failure (network blip, third-party API hiccup) should
      // still get another chance on the next isAuthRoute toggle rather than
      // silently never showing the consent banner for the rest of the tab's
      // session.
      if (hasCheckedLocationRef.current) {
        return;
      }

      try {
        const { data } = await axios.get<LocationResponse>(
          'https://api.country.is/'
        );
        hasCheckedLocationRef.current = true;
        const countryCode = data.country?.toUpperCase();
        if (isMounted && countryCode && countryCode !== US_COUNTRY_CODE) {
          setShowBanner(true);
        }
      } catch (error) {
        console.error('Error fetching user location:', error);
      }
    };

    checkUserLocation();

    return () => {
      isMounted = false;
    };
  }, [isAuthRoute]);

  const acceptCookies = (): void => {
    setCookiesAccepted();
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div
      style={{ position: 'fixed', bottom: 0, width: '100%', zIndex: 999 }}
      className='zoomlogi-cookie-banner fixed bottom-4 left-4 right-4 bg-white shadow-lg p-4 rounded-lg z-50'
    >
      <Card>
        <div
          className='p-4 flex flex-col sm:flex-row items-center'
          style={{ justifyContent: 'space-between' }}
        >
          <p className='text-sm text-gray-700'>
            This website uses cookies to enhance your experience. By continuing,
            you accept our <a href='/privacy-policy'>cookie policy</a>.
          </p>
          <Button onClick={acceptCookies} className='mt-2 sm:mt-0'>
            Accept
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default CookieBanner;
